import { CloseOutlined } from "@ant-design/icons";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import { Navigate } from "react-router-dom";
import { useObject } from "@/entities/object/index.ts";
import { projectQueries } from "@/entities/project/index.ts";
import {
  JournalVersionsListPanel,
  useTitleVersionsStore,
} from "@/features/journal-version-list";
import type { JournalVersion } from "@/features/journal-version-list";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes, useNavigate } from "@/shared/lib/react-router";
import { journalsQueries } from "@entities/journal";
import { Breadcrumbs } from "@shared/ui";
import "./journal-version-title-page.scss";

const { Title } = Typography;

export const JournalVersionTitlePage = () => {
  const isVersionsEnabled = useFlags("ojrFormationFlowEnabledV1");
  const { projectId, objectId, mainJournalId, titleId } = useCoordinator() as {
    projectId: string;
    objectId: string;
    mainJournalId: string;
    titleId: string;
  };
  const navigate = useNavigate();
  const versions = useTitleVersionsStore(
    (s: { versions: JournalVersion[] }) => s.versions,
  );
  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );
  const { data: object } = useObject({ objectId });
  const { data: journalTitle } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  if (!isVersionsEnabled) {
    return <Navigate to=".." replace />;
  }

  const handleVersionClick = (version: JournalVersion) => {
    navigate(
      routes.journals.titleVersionDetail(projectId, objectId, mainJournalId, titleId, version.versionId),
    );
  };

  const journalNumber =
    journalTitle?.mainJournal?.titleJournal?.mainJournalNumber;
  const journalLabel = `Общий журнал работ${journalNumber ? ` №${journalNumber}` : ""}`;

  const breadcrumbs = [
    { path: routes.projects(), label: "Проекты" },
    { path: routes.project.root({ projectId }), label: project.shortName },
    { path: routes.objects.root(projectId), label: "Объекты" },
    {
      path: routes.objects.object(projectId, objectId),
      label: object?.shortName ?? "",
    },
    {
      path: routes.objects.journals(projectId, objectId),
      label: "Журналы",
    },
    {
      path: routes.journals.title(projectId, objectId, mainJournalId, titleId),
      label: journalLabel,
    },
    {
      path: routes.journals.version(projectId, objectId, mainJournalId, titleId),
      label: "Список версий титульного листа",
    },
  ];

  return (
    <div className="general-view-container">
      <div className="journal-version-title-page">
        <div className="journal-version-title-page__header">
          <Breadcrumbs routes={breadcrumbs} />
          <div className="journal-version-title-page__title-row">
            <Title level={3} className="journal-version-title-page__title">
              Список версий титульного листа
            </Title>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => navigate(-1)}
              className="journal-version-title-page__close-btn"
            />
          </div>
        </div>

        <JournalVersionsListPanel
          versions={versions}
          onVersionRowClick={handleVersionClick}
        />
      </div>
    </div>
  );
};
